import React, { useState, useEffect } from "react";
import { Button } from "reactstrap";
import { useDropzone } from "react-dropzone";
import { Check, X } from "react-feather";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FileUploadDisplay from '../../components/FileUploadDisplay';

const UploadImage = ({ onFileSelect, initialFile }) => {
  const [currentFile, setCurrentFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Load initial file if provided
  useEffect(() => {
    if (initialFile && currentFile !== initialFile) {
      // If initialFile is a File object, use it directly
      if (initialFile instanceof File) {
        setCurrentFile(initialFile);
        setImagePreview(URL.createObjectURL(initialFile));
      }
      // If initialFile is a string (S3 URL), set it as current file
      else if (typeof initialFile === "string") {
        setCurrentFile(initialFile);
        setImagePreview(initialFile);
      }
      // If initialFile is an object with file data
      else if (initialFile && typeof initialFile === "object") {
        if (initialFile.url) {
          setCurrentFile(initialFile.url);
          setImagePreview(initialFile.url);
        } else if (initialFile.data) {
          const file = new File(
            [initialFile.data],
            initialFile.name || "profile.jpg",
            { type: "image/jpeg" }
          );
          setCurrentFile(file);
          setImagePreview(URL.createObjectURL(file));
        }
      }
    }
    if (!initialFile && currentFile) {
      setCurrentFile(null);
      setImagePreview(null);
    }
    // eslint-disable-next-line
  }, [initialFile]);

  const {
    isDragActive,
    isDragAccept,
    isDragReject,
    getRootProps,
    getInputProps,
    open,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
      "image/gif": [".gif"],
      "image/webp": [".webp"],
      "image/svg+xml": [".svg"],
    },
    onDrop: (acceptedFiles) => {
      // Only allow files <= 2MB
      const validFiles = acceptedFiles.filter(file => file.size <= 2 * 1024 * 1024);
      if (validFiles.length === 0) {
        toast.error("File too large. Maximum allowed size is 2MB.");
        return;
      }
      const file = validFiles[0];
      setCurrentFile(file);
      setImagePreview(URL.createObjectURL(file));
      if (onFileSelect) {
        onFileSelect(file);
      }
      toast.success("Image selected successfully");
    },
    onDropRejected: (fileRejections) => {
      if (fileRejections && fileRejections.length > 0) {
        toast.error("Only image files (JPEG, PNG, GIF, WEBP, SVG) are allowed.");
      }
    },
  });

  const handleRemoveFile = () => {
    if (imagePreview && currentFile instanceof File) {
      URL.revokeObjectURL(imagePreview);
    }
    setCurrentFile(null);
    setImagePreview(null);
    if (onFileSelect) {
      onFileSelect(null);
    }
    toast.info("Image removed");
  };

  // Clean up preview when component unmounts
  useEffect(() => {
    return () => {
      if (imagePreview && currentFile instanceof File) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview, currentFile]);

  return (
    <>
      <div>
        <div className="imagecard">
          {imagePreview && (
            <Button color="link" onClick={open} className="upload_btn">
              <img src="/assets/images/pencil.svg" alt="" />
            </Button>
          )}
          <div className="dropzone rounded shadow-xxl">
            <div {...getRootProps({ className: "dropzone-upload-wrapper" })} style={{ cursor: 'pointer' }}>
              <input {...getInputProps()} />

              <div className="dropzone-inner-wrapper d-140 rounded dropzone-avatar">
                <div className="">
                  {/* Dropzone state visual feedback */}
                  <div onClick={open}>
                    {isDragAccept && (
                      <div className="rounded overflow-hidden d-140 bg-success text-center font-weight-bold text-white d-flex justify-content-center align-items-center">
                        <Check className="d-40" />
                      </div>
                    )}
                    {isDragReject && (
                      <div className="rounded overflow-hidden d-140 bg-danger text-center font-weight-bold text-white d-flex justify-content-center align-items-center">
                        <X className="d-60" />
                      </div>
                    )}
                    {!isDragActive && !imagePreview && (
                      <div className="upload_detail">
                        <img
                          src="./assets/images/upload.svg"
                          alt="Upload"
                          style={{ width: 32, height: 32, marginBottom: 10 }}
                        />
                        <h4>Upload Photo</h4>
                        <h5>MAX 2MB</h5>
                        <h5>JPG or PNG only</h5>
                      </div>
                    )}
                    {imagePreview && (
                      <div className="rounded overflow-hidden d-140">
                        <img
                          alt="Preview"
                          src={imagePreview}
                          className="preview_img"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {currentFile && (
          <div className="mt-3">
            <FileUploadDisplay
              file={currentFile}
              onRemove={handleRemoveFile}
              showDownload={true}
              showView={true}
              showRemove={true}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default UploadImage;
